import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import ValueDisplayFlipButton from './value-display-flip-button';
import { fetchPortfolios, selectPortfolios, selectStatus } from '../../portfolios-slice';
import { collapseMany } from './collapsible-composite-slice';
import CollapsibleComposite from './collapsible-composite';
import { useAuth0 } from '@auth0/auth0-react';
import { VscCollapseAll } from 'react-icons/vsc';
import PortfolioComposite from '../../../../api/models/portfolioComposite';

/**
 * A collapsible table that shows the asset allocation of the portfolio.
 */
const CollapsiblePortfolioAllocation: React.FC = () => {

  // Get the 'dispatch' method from the store
  const dispatch = useAppDispatch();
  const portfolios = useAppSelector(selectPortfolios);
  const status = useAppSelector(selectStatus);

  const { getAccessTokenSilently } = useAuth0();

  // TODO: add it to user config, using as default 30%
  const deviationLimit = 0.30;

  // React useEffect: applies after rendering
  useEffect(() => {
    if (status === 'idle') {
      const execute = async () => {
        const accessToken = await getAccessTokenSilently();
        dispatch(fetchPortfolios(accessToken));
      }
      execute();
    }
  });

  const isMobile = window.outerWidth < 1024;

  if (status === 'succeeded' && portfolios.length === 0) {
    return (
      <div className='w-full h-72 flex items-center justify-center text-white'>
        <div>
          Ainda não há portfolio criado. Vamos criar um!
        </div>
      </div>
    );
  } else if (status === 'succeeded') {
    const portfolio = portfolios.length > 0 ? portfolios[0] : undefined;

    function collapseAllHandler(event: React.MouseEvent<SVGElement, MouseEvent>): void {
      const allCompositeIds: string[] = [];
      portfolio?.portfolioComposite?.portfolioComponents.forEach(component => {
        if (component instanceof PortfolioComposite && component.id) {
          allCompositeIds.push(component.id);
        }
      })
      dispatch(collapseMany(allCompositeIds));
    }

    return (
      <div className='flex w-full items-start justify-center lg:p-10'>
        <div className='max-h-full w-full lg:w-3/5 lg:rounded-2xl overflow-auto bg-abstract dark:bg-abstract bg-cover text-neutral-200 dark:text-neutral-300 lg:border-2 border-blue-300/40 shadow-2xl shadow-slate-900/80'>
          <table className='text-xs lg:text-sm font-semibold select-none'>
            <thead className='z-30 sticky top-0 uppercase border-b-2 border-blue-300/40 bg-slate-900/75'>
              <tr>
                <th scope='col' className='sticky top-0 text-left pl-2 pr-1 lg:px-3 py-2 lg:py-5 w-1/12'>
                  <VscCollapseAll title='Minimize todas as categorias' size={isMobile ? 20 : 25} onClick={collapseAllHandler} className='cursor-pointer' />
                </th>
                <th scope='col' className='sticky top-0 text-left px-3 lg:px-6 py-2 lg:py-5 w-3/12'>Categoria</th>
                <th scope='col' className='sticky top-0 text-right px-3 lg:px-6 py-2 lg:py-5 w-3/12'>Ativo</th>
                <th scope='col' className='sticky top-0 text-center px-3 py-2 lg:py-5 w-2/12'>
                  <div className='flex items-center justify-center'>
                    <label className='pr-1'>Ideal</label>
                    {<ValueDisplayFlipButton />}
                  </div>
                </th>
                <th scope='col' className='max-lg:hidden sticky top-0 text-center px-3 lg:px-6 py-2 lg:py-5 w-2/12'>Desvio</th>
                <th scope='col' className='max-lg:hidden sticky top-0 text-center px-3 py-2 lg:py-5 w-2/12'>
                  <div className='hidden lg:flex items-center justify-center'>
                    <label className='pr-1'>Atual</label>
                    {<ValueDisplayFlipButton />}
                  </div>
                </th>
                <th scope='col' className='sticky top-0 text-center px-3 lg:px-6 py-2 lg:py-5 w-2/12'>Peso</th>
              </tr>
            </thead>
            <tbody>
              {portfolio?.portfolioComposite ?
                <CollapsibleComposite
                  deviationLimit={deviationLimit}
                  level={1}
                  portfolioComposite={portfolio.portfolioComposite}
                  isLastComponent={true}
                /> : null
              }
            </tbody>
          </table>
        </div>
      </div>
    );
  } else {
    return (<div></div>)
  }
}

export default CollapsiblePortfolioAllocation;